import { InputLabel, MenuItem } from '@mui/material';
import { DateTime } from 'luxon';
import { memo, useEffect } from 'react';
import { PmiSelect } from 'shell/components/shared/pmi-select';
import { PmiFormControl } from 'shell/components/shared/pmi-select/pmi-form-control';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ValoreDominioVO } from 'shell/models/act-wizard';
import { HttpResponse } from 'shell/models/http-response';
import { ItemVM } from 'shell/view-models/item';
import { Operation } from 'shell/view-models/operation';
import { paginationListToQueryString } from 'system/utils/list-object-to-query-string';
import { OperationObject } from 'system/utils/operation-object-to-query-string';

export enum ID_BIRTH_PLACE {
  AbitazionePrivata = 'A1',
  AltroLuogo = 'A2',
  AltroComune = 'A3',
}

export interface ItemBirthPlaceVM extends ItemVM {
  idDominio?: number;
  idValore?: string;
  descrizioneValore?: string;
  codiceStatistico?: string;
}
interface ListBirthPlaceProps {
  disabled?: boolean;
  required?: boolean;
  value: string;
  onChange: (value?: ItemBirthPlaceVM) => void;
  error?: boolean;
  isSmallSize?: boolean;
  idDominio?: string;
  dataReference?: DateTime | null;
}

const ListBirthPlaceComponent = ({
  disabled,
  required,
  onChange,
  value,
  error,
  isSmallSize = false,
  idDominio = '3',
  dataReference,
}: ListBirthPlaceProps): React.ReactElement => {
  const [birthPlaces, getBirthPlaces] = useHttpFetch<
    HttpResponse<Array<ValoreDominioVO>>,
    Array<ItemBirthPlaceVM>
  >(`${__ACT_API_URL__}/valori-dominio-atti`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      params: {},
    },
    transformer: (response) =>
      ((response.data.payload || []) as Array<ValoreDominioVO>).map((item) => ({
        id: item.key?.idValore,
        value: item.descrizioneBreveValore,
        idDominio: item.tipoDominioPadre,
        idValore: item.idValorePadre,
        descrizioneValore: item.valore,
        codiceStatistico: item.codiceStatistico,
      })) as Array<ItemBirthPlaceVM>,
  });

  useEffect(() => {
    const transformedFilters: OperationObject = {
      idDominio: {
        value: idDominio || '',
        operation: Operation.EQUALS,
      },
      dataRiferimento: {
        value: dataReference && dataReference.isValid ? dataReference.toFormat('yyyy-MM-dd') : '',
        operation: Operation.EQUALS,
      },
    };

    const queryString = `${paginationListToQueryString({
      filters: transformedFilters,
      orders: {},
    })}`;

    getBirthPlaces({
      params: {},
      paramsSerializer: () => queryString,
    });
  }, [idDominio, dataReference]);

  const birthPlacesData: Array<ItemBirthPlaceVM> = (birthPlaces?.data ||
    []) as Array<ItemBirthPlaceVM>;

  return (
    <PmiFormControl fullWidth>
      <InputLabel error={error} disabled={disabled} id="birthPlace-label">
        {required ? 'Luogo nascita *' : 'Luogo nascita'}
      </InputLabel>
      <PmiSelect
        labelId="birthPlace-label"
        id="birthPlace"
        name="birthPlace"
        value={value}
        label={required ? 'Luogo nascita *' : 'Luogo nascita'}
        onChange={(event) =>
          onChange(birthPlacesData?.find((item) => item.id === event.target.value))
        }
        disabled={disabled}
        error={error}
        size={isSmallSize ? 'small' : undefined}
      >
        {birthPlacesData?.map((birthPlace: ItemBirthPlaceVM) => (
          <MenuItem key={birthPlace.id} value={birthPlace.id}>
            {birthPlace.value}
          </MenuItem>
        ))}
      </PmiSelect>
    </PmiFormControl>
  );
};

ListBirthPlaceComponent.displayName = 'List Birth Place component';

const ListBirthPlace = memo(ListBirthPlaceComponent);

export default ListBirthPlace as typeof ListBirthPlaceComponent;
